import { createAction } from '@reduxjs/toolkit';
import type * as Types from '../types';
import type * as CartTypes from '@/pages/winkelwagen/types';

/** Example:
const dispatch = useAppDispatch();

const purchaseAction = createPurchaseAction({
  orderId: checkoutResponse.orderNr,
  cartItems: [] // useAppSelector(state => selectLines(state)),
  costs: ...  // useAppSelector(state => selectCosts(state)),
});

dispatch(purchaseAction())
*/

interface IPurchaceEvent {
  orderId: string;
  cartItems: CartTypes.Product[];
  costs?: CartTypes.SummaryTotals;
  isNewUser?: boolean;
}
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#purchase-gtm
export const createPurchaseAction = createAction(
  'gmt/purchase',
  function prepare({ orderId, cartItems, costs, isNewUser = undefined }: IPurchaceEvent) {
    const subTotal = cartItems?.reduce((accumulator: number, current) => {
      return accumulator + current?.costs?.lineAmount?.exTax?.numeric ?? 0;
    }, 0);

    const tax = costs?.vatAmount?.numeric ?? 0;
    const shipment = costs?.shipment?.exTax?.numeric ?? 0;

    const items = cartItems
      .filter((item) => item.quantity > 0)
      .map((item) => {
        return {
          item_id: item.variantCode,
          item_brand: '',
          item_name: item.variantTitle,
          item_category: item.category,
          item_category2: '',
          item_category3: '',
          price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
          quantity: item.quantity,
          ...(isNewUser !== undefined ? { new_user: isNewUser } : {})
        };
      });

    const gtmPurchaseEvent: Types.GtmPurchaseEvent = {
      event: 'purchase',
      ecommerce: {
        transaction_id: orderId,
        value: subTotal,
        tax: tax,
        shipping: shipment,
        currency: 'EUR',
        items
      }
    };

    return {
      meta: {
        queue: true,
        preventDispatch: true
      },
      payload: {
        ...gtmPurchaseEvent
      }
    };
  }
);

export const createViewCartAction = createAction('gmt/view_cart', function prepare({ cartItems }: { cartItems: CartTypes.Product[] }) {
  const subTotal = cartItems?.reduce((accumulator: number, current) => {
    return accumulator + current?.costs?.lineAmount?.exTax?.numeric ?? 0;
  }, 0);

  const items = cartItems.map((item) => {
    return {
      item_id: item.variantCode,
      item_brand: '',
      item_name: item.variantTitle,
      item_category: item.category,
      item_category2: '',
      item_category3: '',
      price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
      quantity: item.quantity
    };
  });

  const gtmViewCartEvent: Types.GtmViewCartEvent = {
    event: 'view_cart',
    ecommerce: {
      value: subTotal,
      currency: 'EUR',
      items
    }
  };

  return {
    payload: {
      ...gtmViewCartEvent
    }
  };
});

export const createAddToCartAction = createAction('gmt/add_to_cart', function prepare({ cartItems }: { cartItems: CartTypes.Product[] }) {
  const subTotal = cartItems?.reduce((accumulator: number, current) => {
    return accumulator + current?.costs?.lineAmount?.exTax?.numeric ?? 0;
  }, 0);

  const items = cartItems.map((item) => {
    return {
      item_id: item.variantCode,
      item_brand: '',
      item_name: item.variantTitle,
      item_category: item.category,
      item_category2: '',
      item_category3: '',
      price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
      quantity: item.quantity
    };
  });

  const gtmViewCartEvent: Types.GtmViewCartEvent = {
    event: 'add_to_cart',
    ecommerce: {
      value: subTotal,
      currency: 'EUR',
      items
    }
  };

  return {
    payload: {
      ...gtmViewCartEvent
    }
  };
});

export const createBeginCheckoutAction = createAction(
  'gmt/begin_checkout',
  function prepare({ cartItems }: { cartItems: CartTypes.Product[] }) {
    const subTotal = cartItems?.reduce((accumulator: number, current) => {
      return accumulator + current?.costs?.lineAmount?.exTax?.numeric ?? 0;
    }, 0);

    const items = cartItems.map((item) => {
      return {
        item_id: item.variantCode,
        item_brand: '',
        item_name: item.variantTitle,
        item_category: item.category,
        item_category2: '',
        item_category3: '',
        price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
        quantity: item.quantity
      };
    });

    const gtmViewCartEvent: Types.GtmViewCartEvent = {
      event: 'begin_checkout',
      ecommerce: {
        value: subTotal,
        currency: 'EUR',
        items
      }
    };

    return {
      payload: {
        ...gtmViewCartEvent
      }
    };
  }
);
